// Weight normalization. Internally everything is TOTAL kg (spec section 7), which
// kills the per-side / total ambiguity that plagued the Notes workflow. The display
// boundary converts to the unit Marko actually enters: per-hand for dumbbells.

import type { Exercise, WeightEntry } from './types';

// Convert a number the user typed (per-hand for per_side, total otherwise) into
// the internal total-kg representation.
export function toTotalKg(entered: number, weightEntry: WeightEntry): number {
  return weightEntry === 'per_side' ? entered * 2 : entered;
}

// Convert internal total kg into the unit shown on screen / in steppers.
export function toDisplayKg(totalKg: number, weightEntry: WeightEntry): number {
  return weightEntry === 'per_side' ? totalKg / 2 : totalKg;
}

// The smallest step in DISPLAY units (what a +/- tap changes on screen). For a
// per_side exercise whose internal increment is 5 kg total, the user sees 2.5.
export function displayIncrement(ex: Pick<Exercise, 'increment' | 'weightEntry'>): number {
  return toDisplayKg(ex.increment, ex.weightEntry);
}

// Round a total-kg weight to the nearest achievable value for this exercise, given
// its increment. Keeps suggested loads on real plate/pin boundaries.
export function snapToIncrement(totalKg: number, increment: number): number {
  if (increment <= 0) return round2(totalKg);
  return round2(Math.round(totalKg / increment) * increment);
}

// Format a weight for display, including the per-hand annotation when relevant.
export function formatWeight(
  totalKg: number,
  ex: Pick<Exercise, 'weightEntry' | 'bodyweight'>,
): string {
  if (ex.bodyweight) {
    const added = round2(totalKg);
    if (added === 0) return 'BW';
    return added > 0 ? `BW +${added}` : `BW ${added}`;
  }
  const shown = round2(toDisplayKg(totalKg, ex.weightEntry));
  return ex.weightEntry === 'per_side' ? `${shown} kg/hand` : `${shown} kg`;
}

// Absolute load used for volume / e1RM. For bodyweight exercises that is
// bodyweight + added; otherwise just the logged total.
export function absoluteLoadKg(
  weightKg: number,
  ex: Pick<Exercise, 'bodyweight'>,
  bodyweightKg: number | null,
  bodyweightFallback = 80,
): number {
  if (!ex.bodyweight) return weightKg;
  return (bodyweightKg ?? bodyweightFallback) + weightKg;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
